import { useState } from 'react'
import {
  Button,
  Checkbox,
  Fa,
  FormItem,
  Modal,
  TextInput,
  showErrorMessage,
  showSuccessMessage,
  showInfoMessage,
} from '@eltoro-ui/components'
import { Orgmanagerv1Org } from 'next-gen-sdk'
import { useAuth } from 'react-oidc-context'
import {
  ErrorMessage,
  getBaseUrl,
  isValidCharLength,
  validCheck,
} from 'Helpers'
import { requestNameChange, pollEmailTriggerStatus } from 'Requests'
import { CCUsersSelector, CCUser } from '../../../../Components/CCUsersSelector'
import { FeatureDisabledMessage } from '../../../../Components'

export const UpdateNameModal = ({
  org,
  closeModal,
}: {
  org: Orgmanagerv1Org
  closeModal: () => void
}) => {
  const [name, setName] = useState(org.name || '')
  const [confirm, setConfirm] = useState(false)
  const [sendingRequest, setSendingRequest] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState<CCUser[]>([])
  const auth = useAuth()

  // Check if name change requests are disabled for this org
  const isNameChangeDisabled = org?.notificationsDisabled || org?.inCollections

  const handleSubmit = async () => {
    setSendingRequest(true)

    if (!org.id) return

    // Get user name from auth profile, fallback to email if name not available
    const userEmail = auth.user?.profile?.email
    const isElToroOperator = userEmail?.includes('@eltoro.com')

    const userName = isElToroOperator
      ? 'An El Toro Operator'
      : auth.user?.profile?.name ||
        auth.user?.profile?.preferred_username ||
        auth.user?.profile?.email ||
        'Unknown User'

    const urlBase = getBaseUrl()

    // Construct the org settings URL
    const orgUrl = `${urlBase}orgs/${org.id}/org-settings`

    try {
      const ccEmails = selectedUsers.map((user) => user.email)

      // Ensure the submitting user's email is included in ccEmails
      if (userEmail && !ccEmails.includes(userEmail)) {
        ccEmails.push(userEmail)
      }
      if (!userEmail) {
        showErrorMessage(
          'Unable to submit organization name change request.',
          'Your user email could not be determined. Please log out and log back in, or contact support@eltoro.com.',
        )
        return
      }

      const response = await requestNameChange(
        userName,
        org.name || '',
        name,
        org.id,
        orgUrl,
        ccEmails,
        [userEmail],
      )

      // Close modal immediately and show snackbar
      closeModal()
      showInfoMessage(
        `Sending organization name change request for "${name}"...`,
        'Please wait while we process your request.',
      )

      // Poll for job completion
      try {
        await pollEmailTriggerStatus(response.jobId)
        showSuccessMessage(
          `Organization name change request for "${name}" has been sent successfully.`,
          'Responses to your request will be sent via email.',
        )
      } catch (pollError) {
        console.error('Error polling job status:', pollError)
        showErrorMessage(
          'Organization name change request was submitted, but we could not confirm if the email was sent.',
          'Please check your email or contact support@eltoro.com.',
        )
      }
    } catch (error) {
      console.error('Error requesting name change:', error)
      showErrorMessage(
        'Failed to submit organization name change request.',
        'Please try again or contact support@eltoro.com.',
      )
    } finally {
      setSendingRequest(false)
    }
  }

  return (
    <Modal
      className="Modal__change-org-name w-[50%]"
      offClick={closeModal}
      header="Change org name"
    >
      {isNameChangeDisabled ? (
        <FeatureDisabledMessage
          featureName="Organization name change requests"
          onClose={closeModal}
        />
      ) : (
        <>
          {sendingRequest && (
            <div className="UpdateNameModal__sending bg-base absolute top-0 bottom-0 right-0 left-0 z-40 overflow-clip p-2 opacity-60">
              <Fa
                className="text-tint-khaki"
                icon="spinner"
                size={6}
                animate="spin"
              />
            </div>
          )}
          <div className="Modal__change-org-name flex flex-col gap-2 p-1">
            <p>
              Please enter the updated name for <strong>{org.name}</strong>:
            </p>
            <FormItem
              htmlFor="new-org-name"
              label={<p className="mb-2 text-lg font-bold">Requested Name</p>}
              required
              errorMessage={ErrorMessage({
                fieldName: name,
                max: 255,
                label: 'Org name',
              })}
              valid={validCheck(name) && isValidCharLength(name, 255)}
              wrapperClassname="flex-col"
            >
              <TextInput
                valid={isValidCharLength(name, 255)}
                hideValidIcon
                maxLength={256}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </FormItem>
            {name !== org.name && (
              <div className="Modal__change-org-name--confirm-note animate-slidedown flex flex-col gap-1 pt-4">
                {!confirm ? (
                  <p className="bg-warning animate-slidedown rounded px-2 py-1 text-base font-bold">
                    Confirm that you want to change your org's name
                  </p>
                ) : (
                  <p className="text-tint-gray-800 animate-slideleft ml-auto py-2 font-bold">
                    It will take at least a day to process your request
                  </p>
                )}
                <Checkbox
                  label={
                    !confirm ? (
                      <span>
                        Change org's name to<strong> {name}</strong>
                      </span>
                    ) : (
                      <span className="animate-slideleft font-bold">
                        Confirmed. Please change org's name to
                        <strong> {name}</strong>
                      </span>
                    )
                  }
                  checked={confirm}
                  onChange={setConfirm}
                  classNameWrapperLabel="max-w-[unset]"
                  disabled={name.length > 255 || name === org.name}
                />
              </div>
            )}

            {name !== org.name && confirm && (
              <div className="animate-slidedown mt-4">
                <CCUsersSelector
                  selectedUsers={selectedUsers}
                  onSelectedUsersChange={setSelectedUsers}
                />
              </div>
            )}

            <div className="UpdateNameModal__buttons mt-2 ml-auto flex gap-4">
              <Button onClick={() => closeModal()}>Cancel</Button>
              <Button
                kind="primary"
                disabled={name.length > 255 || name === org.name || !confirm}
                onClick={handleSubmit}
              >
                Send request
              </Button>
            </div>
          </div>
        </>
      )}
    </Modal>
  )
}
